import { CircularProgress } from '@material-ui/core'
import debounce from 'just-debounce'
import Mustache from 'mustache'
import { ComponentProps, forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

import {
  CategoryDictionary,
  FaqSettingMinimal,
  FilterSearchResultLayout,
  Hierarchy,
  SearchPageContext,
  SearchParams,
} from '@karakuri-web/faq-type'

import { Pagination, QAContent, QAHeader, QAPanel } from '../components'
import { useFaqData } from '../contexts/FaqDataContext'
import { useFaqRouting } from '../contexts/FaqRoutingContext'

interface Card {
  id: string
  title: string
  confidence?: number
}
interface SearchResult {
  currentPage: number
  perPage: number
  total: number
  totalPages: number
  data: Card[]
}

/**
 * FilterSearchResultWidget用の設定インターフェース
 */
export interface FilterSearchResultWidgetSettings extends FaqSettingMinimal {
  waitMs?: {
    search?: number | null
  } | null
  searchType?: string | null
  queryThreshold?: number | null
  maybeThisOneThreshold?: number | null
  words?: {
    search?: {
      numberOfItems?: string | null
      resultMessage?: {
        success?: string | null
      } | null
    } | null
  } | null
}

interface PageProps<D, C, S> {
  data: D
  pageContext: C
  path: string
  location?: { search: string }
  state?: S
}

type Props<T extends FilterSearchResultWidgetSettings = FilterSearchResultWidgetSettings> = Pick<
  PageProps<{ settingYaml: T }, SearchPageContext, SearchParams>,
  'data' | 'pageContext' | 'path'
> & {
  layout: FilterSearchResultLayout
  dictionary: CategoryDictionary
  q: string[]
  page: number
  onSearch: (q: string[], page: number) => void
  botId: string
  tenantId?: string | null
}

/**
 * 絞り込み検索結果ウィジェット
 * 検索結果を表示する
 */
export function FilterSearchResultWidget<
  T extends FilterSearchResultWidgetSettings = FilterSearchResultWidgetSettings,
>({ layout, dictionary, q, page, onSearch, pageContext, botId, tenantId, data, path }: Props<T>) {
  const { Link } = useFaqRouting()
  const faqData = useFaqData()
  const waitMs = data.settingYaml.waitMs?.search || 500
  const nrOfItemsTemplate =
    data.settingYaml.words?.search?.numberOfItems ||
    '{{total}}件のうち {{start}}-{{end}}件 を表示しています'

  const maybeThisOneThreshold = data.settingYaml.maybeThisOneThreshold || 1
  const [loading, setLoading] = useState(false)
  const [searchResult, setSearchResult] = useState<{ search: SearchResult } | null>(null)
  const [results, setResults] = useState<Card[]>([])
  const [count, setCount] = useState(1)

  const memoizedSearch = useMemo(
    () =>
      debounce(
        async (q: string[], page: number) => {
          setLoading(true)
          try {
            const result = await faqData.filterSearch({
              botId,
              q,
              page,
              tags: pageContext?.category?.id
                ? [pageContext.category.id, ...pageContext.children]
                : [],
              searchType: data.settingYaml.searchType || undefined,
              queryThreshold: data.settingYaml.queryThreshold || undefined,
              tenantId,
            })
            setSearchResult(result)
          } finally {
            setLoading(false)
          }
        },
        waitMs,
        false,
        true
      ),
    [
      botId,
      tenantId,
      faqData,
      data.settingYaml.queryThreshold,
      data.settingYaml.searchType,
      pageContext?.category?.id,
      pageContext?.children,
      waitMs,
    ]
  )
  const search = useCallback(
    (q: string[], page: number) => memoizedSearch(q, page),
    [memoizedSearch]
  )
  useEffect(() => {
    search(q, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page])
  useEffect(() => {
    if (searchResult?.search?.data) {
      setResults(searchResult.search.data)
      setCount(searchResult.search.totalPages)
      faqData.sendFilterSearchEvent({ inputValue: q.join(' '), count: searchResult.search.total })
      faqData.recordSearched({ botId, tenantId, text: q.join(' ') })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult?.search?.data])

  const title = pageContext.category?.title
    ? `${pageContext.category?.title}のご質問`
    : layout.title || '検索結果'
  return (
    <QAPanel data-testid="faq-filter-search-result-panel">
      {loading && (
        <div className="faq-loading">
          <CircularProgress />
        </div>
      )}
      <QAHeader title={title} />
      {numberOfItems(nrOfItemsTemplate, searchResult?.search, data)}
      {results.map(({ id, title, confidence }) => {
        const LinkComponent = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>(
          function LinkComponent(props, ref) {
            return (
              <Link
                ref={ref}
                {...props}
                to={`/answer/${id}/?search=true`}
                state={{
                  hierarchies: path
                    .split('/')
                    .map(p => (dictionary || {})[p])
                    .filter(c => !!c)
                    .reduce<Hierarchy[]>((acc, c) => {
                      const current =
                        acc.length === 0 ? '/search' : acc[acc.length - 1].link.replace(/\/$/, '')
                      acc.push({ title: c.title || '', link: `${current}/${c.id}/` })
                      return acc
                    }, []),
                  q,
                  page,
                  tags: pageContext?.category?.id
                    ? [pageContext.category.id, ...pageContext.children]
                    : [],
                }}
              />
            )
          }
        )
        return (
          <QAContent
            key={id}
            question={title}
            LinkComponent={LinkComponent}
            className={(confidence || 0) > maybeThisOneThreshold ? 'maybe-this-one' : ''}
          />
        )
      })}
      {count > 1 && <Pagination page={page} count={count} onChange={page => onSearch(q, page)} />}
    </QAPanel>
  )
}

function numberOfItems<T extends FilterSearchResultWidgetSettings>(
  template: string,
  search?: SearchResult,
  data?: { settingYaml: T }
) {
  if (!search) {
    return null
  }
  const total = search.total
  if (!total) {
    if (data?.settingYaml.words?.search?.resultMessage?.success) {
      return (
        <p className="faq-filter-search-result-widget__result-message-success">
          {data.settingYaml.words.search.resultMessage.success}
        </p>
      )
    }
    return null
  }

  const base = (search.currentPage - 1) * search.perPage
  const start = base + 1
  const end = Math.min(total, base + search.perPage)
  return <p className="faq-number-of-items">{Mustache.render(template, { total, start, end })}</p>
}
